import React, { useEffect, useId, useRef, useState } from 'react';
import { AdjustmentsHorizontalIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { SegmentedControl } from '../ui';
import {
  DEFAULT_EXPORT_OPTIONS,
  EXPORT_FORMATS,
  EXPORT_GROUPS,
  getExportFormat,
  isDefaultExportOptions,
} from '../../utils/contextExport';

/** Compact "Text ▾" button with a popover to pick the copy/download format and included field groups. */
export default function ExportFormatPicker({ options, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const panelId = useId();
  const format = getExportFormat(options.format);
  const groupsCustomized = EXPORT_GROUPS.some(({ id }) => options.groups[id] !== DEFAULT_EXPORT_OPTIONS.groups[id]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointer = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
    };
    const onKey = (event) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('touchstart', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('touchstart', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const setGroup = (id, checked) => onChange({ ...options, groups: { ...options.groups, [id]: checked } });

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-label={`Export format: ${format.label}`}
        title="Copy & download format"
        onClick={() => setOpen((value) => !value)}
        className="flex h-11 items-center gap-1.5 whitespace-nowrap rounded-lg bg-[#F3F4F6] px-3 text-sm font-medium text-[#374151] transition-colors hover:bg-[#E5E7EB] dark:bg-[#374151] dark:text-[#D1D5DB] dark:hover:bg-[#4B5563]"
      >
        <AdjustmentsHorizontalIcon className="h-4 w-4" aria-hidden="true" />
        {format.short}
        {groupsCustomized && <span className="h-1.5 w-1.5 rounded-full bg-[#3B82F6]" aria-hidden="true" />}
        <ChevronDownIcon className="h-3.5 w-3.5 opacity-70" aria-hidden="true" />
      </button>
      {open && (
        <div
          id={panelId}
          role="dialog"
          aria-label="Copy and download format"
          className="absolute left-0 z-50 mt-1 w-64 rounded-panel border border-line bg-surface-raised p-3 shadow-pop"
        >
          <p className="mb-2 text-xs font-semibold text-fg-muted">Format</p>
          <SegmentedControl
            label="Format"
            size="sm"
            fullWidth
            value={options.format}
            onChange={(value) => onChange({ ...options, format: value })}
            options={EXPORT_FORMATS.map(({ value, short }) => ({ value, label: short }))}
          />
          <p className="mb-1 mt-3 text-xs font-semibold text-fg-muted">Include</p>
          <div className="flex flex-col">
            {EXPORT_GROUPS.map(({ id, label }) => (
              <label key={id} className="flex cursor-pointer items-center gap-2.5 py-1.5 text-sm text-fg">
                <input
                  type="checkbox"
                  checked={!!options.groups[id]}
                  onChange={(event) => setGroup(id, event.target.checked)}
                  className="h-4 w-4 cursor-pointer accent-[#3B82F6]"
                />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-2 flex items-center justify-between gap-2 border-t border-line pt-2">
            <p className="text-xs text-fg-muted">Used by every copy and download here.</p>
            {!isDefaultExportOptions(options) && (
              <button
                type="button"
                onClick={() => onChange(DEFAULT_EXPORT_OPTIONS)}
                className="shrink-0 text-xs font-medium text-[#3B82F6] hover:underline dark:text-[#60A5FA]"
              >
                Reset
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
